/*
* PieChart.jsx
* Copyright (c) Harman International Industries, Incorporated, 2024 - 2025
* All rights reserved.
*/
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import PropTypes from "prop-types";
import { PieChart } from "@mui/x-charts/PieChart";
import { useEffect, useState } from "react";
import { useDrawingArea } from "@mui/x-charts/hooks";
import { styled } from "@mui/material/styles";

const StyledText = styled("text")(({ theme }) => ({
  fill: theme.palette.text.primary,
  textAnchor: "middle",
  dominantBaseline: "central",
  fontSize: 20,
}));

function PieCenterLabel({ children }) {
  const { width, height, left, top } = useDrawingArea();
  return (
    <StyledText x={left + width / 2} y={top + height / 2}>
      {children}
    </StyledText>
  );
}

PieCenterLabel.propTypes = {
  children: PropTypes.node.isRequired,
};

const truncateDistributionLabel = (label) =>
  label.length > 7 ? `${label.substring(0, 7)}...` : label;

const convertToChartData = (items) =>
  items.map((ele) => ({
    label: ele.distributionLabel,
    value: ele.percentage,
  }));

const processDistributionList = (list) => {
  if (!list?.length || list[0].percentage === 0) return null;
  const truncated = list.map((ele) => ({
    ...ele,
    distributionLabel: truncateDistributionLabel(ele.distributionLabel),
  }));
  return convertToChartData(truncated);
};

export const PieChartComponent = ({ barAndPieObjData }) => {
  const [installsByVehicle, setInstallsByVehicle] = useState([]);
  const [uninstallsByVehicle, setUninstallsByVehicle] = useState([]);
  const [installsByCountry, setInstallsByCountry] = useState([]);
  const [UninstallsByCountry, setUninstallsByCountry] = useState([]);
  const [installsByAppVersion, setInstallsByAppVersion] = useState([]);
  const [UninstallsByAppVersion, setUninstallsByAppVersion] = useState([]);

  const [alignment, setAlignment] = useState("installs");
  const [showInstallsPieChart, setShowInstallsPieChart] = useState(true);
  const [showuninstallsPieChart, setShowuninstallsPieChart] = useState(false);


  const handleChange = (event, newAlignment) => {
    setAlignment(newAlignment);

    switch (newAlignment) {
      case "installs":
        setShowInstallsPieChart(true);
        setShowuninstallsPieChart(false);
        break;
      case "uninstalls":
        setShowuninstallsPieChart(true);
        setShowInstallsPieChart(false);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (!barAndPieObjData?.distributionDTO || Object.keys(barAndPieObjData.distributionDTO).length === 0) return;
    const { distributionByVehicle, distributionByCountry, distributionByAppVersion } = barAndPieObjData.distributionDTO;

    const iv = processDistributionList(distributionByVehicle.install);
    if (iv) setInstallsByVehicle(iv);

    const uv = processDistributionList(distributionByVehicle.unInstall);
    if (uv) setUninstallsByVehicle(uv);

    const ic = processDistributionList(distributionByCountry.install);
    if (ic) setInstallsByCountry(ic);

    const uc = processDistributionList(distributionByCountry.unInstall);
    if (uc) setUninstallsByCountry(uc);

    const iav = processDistributionList(distributionByAppVersion.install);
    if (iav) setInstallsByAppVersion(iav);

    const uav = processDistributionList(distributionByAppVersion.unInstall);
    if (uav) setUninstallsByAppVersion(uav);
  }, [barAndPieObjData]);

  return (
    <div>
      <Card
        sx={{
          width: 875,
          height: 300,
          boxShadow: "none",
          border: "1px solid #12141533",
        }}
      >
        <CardHeader
          action={
            <ToggleButtonGroup
              color="primary"
              value={alignment}
              exclusive
              onChange={handleChange}
              aria-label="Platform"
            >
              <ToggleButton className="actionBtnCls" value="installs">
                Installs
              </ToggleButton>
              <ToggleButton className="actionBtnCls" value="uninstalls">
                Uninstalls
              </ToggleButton>
            </ToggleButtonGroup>
          }
          title="Distribution"
          sx={{
            padding: "3px",
            paddingLeft: "10px",
            fontWeight: 600,
          }}
          disableTypography
        />
        <Divider sx={{ marginTop: "0px !important" }} />
        <CardContent>
          {showInstallsPieChart && (
            <div className="cardCls">
              <Card
                  sx={{
                    width: 340,
                    height: 200,
                    boxShadow: "none",
                    border: "1px solid #12141533",
                  }}
                >
                  <CardHeader title="By App versions" disableTypography />
                  <Divider sx={{ marginTop: "0px !important" }} />
                  <CardContent
                    sx={{ textAlign: "center", padding: "0px !important" }}
                  >
                    <PieChart
                      series={[
                        {
                          data: installsByAppVersion,
                          innerRadius: 45,
                          outerRadius: 55,
                        },
                      ]}
                      {...pieParams}
                    >
                      {installsByAppVersion.length > 0 && (
                        <PieCenterLabel>100%</PieCenterLabel>
                      )}
                    </PieChart>
                  </CardContent>
                </Card>
                 <Card
                sx={{
                  width: 340,
                  height: 200,
                  boxShadow: "none",
                  border: "1px solid #12141533",
                }}
              >
                <CardHeader title="By Country" disableTypography />
                <Divider sx={{ marginTop: "0px !important" }} />
                <CardContent
                  sx={{ textAlign: "center", padding: "0px !important" }}
                >
                  <PieChart
                    series={[
                      {
                        data: installsByCountry,
                        innerRadius: 45,
                        outerRadius: 55,
                      },
                    ]}
                    {...pieParams}
                  >
                    {installsByCountry.length > 0 && (
                      <PieCenterLabel>100%</PieCenterLabel>
                    )}
                  </PieChart>
                </CardContent>
              </Card>
              <Card
                sx={{
                  width: 340,
                  height: 200,
                  boxShadow: "none",
                  border: "1px solid #12141533",
                }}
              >
                <CardHeader title="By Make" disableTypography />
                <Divider sx={{ marginTop: "0px !important" }} />
                <CardContent
                  sx={{ textAlign: "center", padding: "0px !important" }}
                >
                  <PieChart
                    series={[
                      {
                        data: installsByVehicle,
                        innerRadius: 45,
                        outerRadius: 55,
                      },
                    ]}
                    {...pieParams}
                  >
                    {installsByVehicle.length > 0 && (
                      <PieCenterLabel>100%</PieCenterLabel>
                    )}
                  </PieChart>
                </CardContent>
              </Card>
             
              {
                
              }
            </div>
          )}
          {showuninstallsPieChart && (
            <div className="cardCls">
              <Card
                sx={{
                  width: 340,
                  height: 200,
                  boxShadow: "none",
                  border: "1px solid #12141533",
                }}
              >
                <CardHeader title="By App versions" disableTypography />
                <Divider sx={{ marginTop: "0px !important" }} />
                <CardContent
                  sx={{ textAlign: "center", padding: "0px !important" }}
                >
                  <PieChart
                    series={[
                      {
                        data: UninstallsByAppVersion,
                        innerRadius: 45,
                        outerRadius: 55,
                      },
                    ]}
                    sx={{
                      "&.MuiChartsLegend-root": {
                        overflow: "hidden !important",
                        whiteSpace: "nowrap !important",
                        textOverflow: "ellipsis !important",
                      },
                    }}
                    {...pieParams}
                  >
                    {UninstallsByAppVersion.length > 0 && (
                      <PieCenterLabel>100%</PieCenterLabel>
                    )}
                  </PieChart>
                </CardContent>
              </Card>
              <Card
                sx={{
                  width: 340,
                  height: 200,
                  boxShadow: "none",
                  border: "1px solid #12141533",
                }}
              >
                <CardHeader title="By Country" disableTypography />
                <Divider sx={{ marginTop: "0px !important" }} />
                <CardContent
                  sx={{ textAlign: "center", padding: "0px !important" }}
                >
                  <PieChart
                    series={[
                      {
                        data: UninstallsByCountry,
                        innerRadius: 45,
                        outerRadius: 55,
                      },
                    ]}
                    {...pieParams}
                  >
                    {UninstallsByCountry.length > 0 && (
                      <PieCenterLabel>100%</PieCenterLabel>
                    )}
                  </PieChart>
                </CardContent>
              </Card>
              <Card
                sx={{
                  width: 340,
                  height: 200,
                  boxShadow: "none",
                  border: "1px solid #12141533",
                }}
              >
                <CardHeader title="By Make" disableTypography />
                <Divider sx={{ marginTop: "0px !important" }} />
                <CardContent
                  sx={{ textAlign: "center", padding: "0px !important" }}
                >
                  <PieChart
                    series={[
                      {
                        data: uninstallsByVehicle,
                        innerRadius: 45,
                        outerRadius: 55,
                      },
                    ]}
                    {...pieParams}
                  >
                    {uninstallsByVehicle.length > 0 && (
                      <PieCenterLabel>100%</PieCenterLabel>
                    )}
                  </PieChart>
                </CardContent>
              </Card>
              
              
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

PieChartComponent.propTypes = {
  barAndPieObjData: PropTypes.object.isRequired,
};

const pieParams = {
  height: 120,
  margin: { left: 20, right: 150 },
  slotProps: { 
    legend: { 
      hidden: false,
      direction: 'column',
      position: { vertical: 'middle', horizontal: 'right' },
      padding: 5,
      itemMarkWidth: 10,
      itemMarkHeight: 10,
      markGap: 5,
      itemGap: 3,
      labelStyle: {
        fontSize: 10,
        fill: '#333'
      },
      itemsSpacing: 2
    } 
  },
};
